import type {SerializablePlainData} from './SerializablePlainData';

const omittedFeatureHistoryProperty = Symbol('omittedFeatureHistoryProperty');

type NormalizedFeatureHistoryValue = SerializablePlainData | typeof omittedFeatureHistoryProperty;

/**
 * 判断对象是否为普通对象。
 *
 * @param value 待判断对象。
 * @returns 是否为普通对象。
 */
function isPlainObject(value: object): boolean {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

/**
 * 判断对象是否为禁止进入 feature 历史快照的运行时容器。
 *
 * @param value 待判断对象。
 * @returns 是否为禁止进入历史的对象。
 */
function isUnsupportedRuntimeContainer(value: object): boolean {
    return value instanceof Date
        || value instanceof Map
        || value instanceof Set
        || value instanceof WeakMap
        || value instanceof WeakSet
        || value instanceof RegExp
        || value instanceof Error
        || value instanceof ArrayBuffer
        || ArrayBuffer.isView(value);
}

/**
 * 递归归一化 feature 命令参数值。
 *
 * 设计说明：
 * 1. Feature history 只能保存可 replay 的主状态，不允许保存 Worker、BRep、Mesh、Three.js 等运行时对象；
 * 2. 拉伸等命令参数中可能携带 Vector-like class 实例，这类对象只要由可枚举字段组成，就可以降级为 plain object；
 * 3. 对象属性中的 undefined 与 JSON 序列化语义一致，直接省略，避免 redo 时被 plain data 校验拒绝。
 *
 * @param value 待归一化值。
 * @param path 当前字段路径，用于错误定位。
 * @param visited 已访问对象集合，用于拒绝循环引用。
 * @returns 可写入历史快照的 plain data，或表示对象属性应省略的标记。
 */
function normalizeFeatureHistoryValue(
    value: unknown,
    path: string,
    visited: WeakSet<object>,
): NormalizedFeatureHistoryValue {
    if (typeof value === 'undefined') {
        return omittedFeatureHistoryProperty;
    }

    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return value as SerializablePlainData;
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            throw new Error(`Feature 历史参数 ${path} 不是有限数字，不能写入 undo/redo 快照。`);
        }

        return value;
    }

    if (typeof value === 'bigint' || typeof value === 'symbol' || typeof value === 'function') {
        throw new Error(`Feature 历史参数 ${path} 不是可序列化 plain data。`);
    }

    if (Array.isArray(value)) {
        return value.map((item, index): SerializablePlainData => {
            const normalizedItem = normalizeFeatureHistoryValue(item, `${path}[${index}]`, visited);

            if (normalizedItem === omittedFeatureHistoryProperty) {
                throw new Error(`Feature 历史参数 ${path}[${index}] 是 undefined，数组项不能省略。`);
            }

            return normalizedItem;
        });
    }

    const objectValue = value as object;

    if (isUnsupportedRuntimeContainer(objectValue)) {
        throw new Error(`Feature 历史参数 ${path} 包含运行时容器，不能写入 undo/redo 快照。`);
    }

    if (visited.has(objectValue)) {
        throw new Error(`Feature 历史参数 ${path} 存在循环引用，不能写入 undo/redo 快照。`);
    }

    visited.add(objectValue);

    const entries = Object.entries(objectValue as Record<string, unknown>);

    if (!isPlainObject(objectValue) && entries.length === 0) {
        throw new Error(`Feature 历史参数 ${path} 是无可枚举主状态的运行时对象，不能写入 undo/redo 快照。`);
    }

    const normalizedRecord: Record<string, SerializablePlainData> = {};

    entries.forEach(([key, childValue]) => {
        const normalizedChild = normalizeFeatureHistoryValue(childValue, `${path}.${key}`, visited);

        if (normalizedChild !== omittedFeatureHistoryProperty) {
            normalizedRecord[key] = normalizedChild;
        }
    });

    visited.delete(objectValue);

    return normalizedRecord;
}

/**
 * 将 feature 命令参数归一化为可序列化 plain record。
 *
 * @param params feature 命令参数。
 * @returns 可安全写入 history ChangeSet 的参数快照。
 */
export function normalizeFeatureHistoryParameterRecord(
    params: Readonly<Record<string, unknown>>,
): Readonly<Record<string, SerializablePlainData>> {
    const normalized = normalizeFeatureHistoryValue(params, 'params', new WeakSet<object>());

    if (
        normalized === omittedFeatureHistoryProperty
        || Array.isArray(normalized)
        || typeof normalized !== 'object'
        || normalized === null
    ) {
        throw new Error('Feature 历史参数根节点必须是 plain record。');
    }

    return normalized as Readonly<Record<string, SerializablePlainData>>;
}
